<template>
  <el-dialog
    v-model="showModal"
    :class="`zakat-donation-modal donation-style-1`"
    :modal-append-to-body="true"
    :append-to-body="true"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    @close="closeModal"
    modal-class="wpcm-wrapper"
    width="500px"
  >
    <template #header>
      <div class="zakat-modal-header">
        <h3>Zakat Donation</h3>
      </div>
    </template>

    <div class="zakat-modal-content">
      <!-- Zakat Amount Form -->
      <ZakatDonationForm 
        :zakat-amount="amount"
        :currencies="store.config.currencies"
        :symbols="store.config.symbols"
        :symbol="store.config.symbol"
        :show_currency_dropdown="false"
      />

      <!-- Billing Information -->
      <div class="billing-section">
        <lifeline-donation-billing-form 
          :strings="store.config.strings"
          :countries="store.config.countries"
          :states="store.config.states"
        />
      </div>

      <!-- Payment Gateways -->
      <div class="payment-section">
        <lifeline-donation-gateways 
          :gateways="store.config.gateways"
          :strings="store.config.strings"
        />
      </div>

      <!-- Proceed Button -->
      <div class="proceed-section">
        <lifeline-donation-proceed-btn 
          :strings="store.config.strings"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useDonationFormsStore } from '../store';
import ZakatDonationForm from './ZakatDonationForm.vue';

const store = useDonationFormsStore();

const props = defineProps({
  amount: {
    type: Number,
    required: true
  },
  currency: {
    type: String,
    default: 'USD'
  },
  postId: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['close', 'success']);

const showModal = ref(true);

const closeModal = () => {
  showModal.value = false;
  emit('close');
};

onMounted(async () => {
  // Initialize the store with Zakat-specific data
  await store.getData({ 
    vm: { 
      id: props.postId,
      dstyle: 1 // Use style 1 for Zakat donations
    } 
  });
  
  // Set the Zakat amount and purpose
  store.amount = props.amount;
  store.extras.donation_purpose = 'Zakat';
  store.currency = props.currency;
  
  // Disable recurring donations for Zakat
  store.recurring = false;
  
  // Show the modal
  store.showModal = true;
});
</script>

<style scoped>
.zakat-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.zakat-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.zakat-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
}

.zakat-modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 20px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #333;
}

.zakat-modal-body {
  padding: 24px;
}

.zakat-amount-display {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 8px;
  margin-bottom: 24px;
}

.amount-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.amount-value {
  font-size: 32px;
  font-weight: bold;
  color: #28a745;
  margin-bottom: 8px;
}

.currency {
  font-size: 24px;
}

.amount-note {
  font-size: 12px;
  color: #888;
}

.donor-info, .payment-method {
  margin-bottom: 24px;
}

.donor-info h4, .payment-method h4 {
  margin: 0 0 16px 0;
  color: #333;
  font-size: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-row .form-group {
  flex: 1;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #28a745;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.1);
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.3s;
}

.payment-option:hover {
  border-color: #28a745;
  background: #f8f9fa;
}

.payment-option input[type="radio"] {
  margin: 0;
}

.zakat-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #eee;
}

.btn-cancel, .btn-donate {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-cancel {
  background: #f8f9fa;
  border: 1px solid #ddd;
  color: #666;
}

.btn-cancel:hover {
  background: #e9ecef;
}

.btn-donate {
  background: linear-gradient(135deg, #28a745, #20c997);
  border: none;
  color: white;
}

.btn-donate:hover:not(:disabled) {
  background: linear-gradient(135deg, #218838, #1ea080);
}

.btn-donate:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.currency-selection {
  margin-bottom: 24px;
}

.currency-selection label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
}

.currency-selection select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}
</style>